import React, { useEffect, useState } from "react";
import { DownloadSimple, X } from "@phosphor-icons/react";

// Cross-platform "Install Sent-i-meter" prompt.
//
// • On Android/Chrome/Edge we capture the `beforeinstallprompt` event and surface
//   a native install button.
// • On iOS Safari (which has no install event) we show a hint banner explaining
//   the "Share → Add to Home Screen" flow.
// • Dismissals are remembered in localStorage so we don't nag users.
const DISMISS_KEY = "sentimeter_pwa_install_dismissed_v1";

function isIos() {
  if (typeof window === "undefined") return false;
  const ua = window.navigator.userAgent || "";
  return /iPhone|iPad|iPod/i.test(ua) && !/CriOS|FxiOS/i.test(ua);
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true
  );
}

export default function PWAInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return; // already installed
    if (localStorage.getItem(DISMISS_KEY)) return;

    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBefore);

    const onInstalled = () => {
      setShow(false);
      setIosHint(false);
      setDeferred(null);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari has no beforeinstallprompt — show a soft hint instead.
    if (isIos()) {
      const t = setTimeout(() => setIosHint(true), 4000);
      return () => {
        clearTimeout(t);
        window.removeEventListener("beforeinstallprompt", onBefore);
        window.removeEventListener("appinstalled", onInstalled);
      };
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBefore);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } finally {
      setDeferred(null);
      setShow(false);
    }
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, "1");
    setShow(false);
    setIosHint(false);
  };

  if (!show && !iosHint) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] bg-black border border-white/15 shadow-[0_8px_24px_rgba(0,0,0,0.5)] backdrop-blur-xl"
      data-testid="pwa-install-prompt"
    >
      <div className="flex items-start gap-3 p-4">
        <div className="w-10 h-10 bg-white text-black flex items-center justify-center shrink-0">
          <DownloadSimple size={18} weight="bold" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="heading text-sm font-semibold tracking-tight">Install Sent-i-meter</div>
          {show ? (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Add the app to your home screen for full-screen access, faster loads and offline shell.
            </p>
          ) : (
            <p className="text-xs text-white/70 mt-1 leading-relaxed">
              Tap <span className="mono text-neutral_zone">Share</span> →{" "}
              <span className="mono text-neutral_zone">Add to Home Screen</span> to install Sent-i-meter.
            </p>
          )}
          <div className="flex items-center gap-2 mt-3">
            {show && (
              <button
                onClick={install}
                className="px-3 py-1.5 bg-white text-black mono text-[10px] uppercase tracking-[0.18em] hover:bg-white/90 transition-colors duration-150"
                data-testid="pwa-install-btn"
              >
                Install
              </button>
            )}
            <button
              onClick={dismiss}
              className="px-3 py-1.5 border border-white/15 text-white/70 hover:text-white hover:border-white/40 mono text-[10px] uppercase tracking-[0.18em] transition-colors duration-150"
              data-testid="pwa-install-dismiss"
            >
              Not now
            </button>
          </div>
        </div>
        <button
          onClick={dismiss}
          className="text-white/40 hover:text-white shrink-0"
          aria-label="dismiss"
        >
          <X size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}
